import { createFileRoute } from "@tanstack/react-router";
import { SeedSprout } from "@/components/SeedSprout";
import { SiteNav, SiteFooter } from "@/components/SiteChrome";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Akram Fortas — Future Farmer" },
      {
        name: "description",
        content:
          "Not a CEO. Not a guru. Just a Future Farmer. The personal garden of Akram Fortas — digital marketer, entrepreneur, and lifelong experimenter.",
      },
      { property: "og:title", content: "Akram Fortas — Future Farmer" },
      {
        property: "og:description",
        content:
          "Planting digital seeds and hoping for actual rain. A quiet corner of the internet.",
      },
      { property: "og:url", content: "https://akramfortas.com/" },
      {
        property: "og:image",
        content:
          "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/fb0056ea-00ca-4faa-a190-7f512c95a0ce/id-preview-68e78fa1--3cca79cf-7e9e-4db2-bbe1-4fc5d612bc83.lovable.app-1782157770798.png",
      },
      {
        name: "twitter:image",
        content:
          "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/fb0056ea-00ca-4faa-a190-7f512c95a0ce/id-preview-68e78fa1--3cca79cf-7e9e-4db2-bbe1-4fc5d612bc83.lovable.app-1782157770798.png",
      },
      { name: "twitter:title", content: "Akram Fortas — Future Farmer" },
      {
        name: "twitter:description",
        content: "Planting digital seeds and hoping for actual rain.",
      },
    ],
    links: [{ rel: "canonical", href: "https://akramfortas.com/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Akram Fortas",
          url: "https://akramfortas.com",
          jobTitle: "Digital marketer, entrepreneur, experimenter",
          description:
            "Not a CEO. Not a guru. Just a Future Farmer planting digital seeds on the internet.",
          sameAs: [
            "https://linkedin.com/in/fopreneur",
            "https://x.com/fopreneur",
            "https://instagram.com/fopreneur",
          ],
        }),
      },
    ],
  }),
  component: Home,
});

const planting = [
  {
    title: "Learning systems thinking",
    desc: "Working through Donella Meadows, slowly. Trying to see leverage points everywhere.",
    status: "🌱 Planting",
  },
  {
    title: "A small marketing studio",
    desc: "Building a tiny practice around honest positioning — no funnels, no fake urgency.",
    status: "🌿 Growing",
  },
  {
    title: "Writing more, posting less",
    desc: "Notes in public, but unhurried. Trying to think clearly before I publish.",
    status: "🧪 Testing",
  },
  {
    title: "A second income stream",
    desc: "Quiet experiments with productized services. Most won't work. That's fine.",
    status: "☀️ Waiting for rain",
  },
  {
    title: "Three months in Southeast Asia",
    desc: "Slower mornings, longer walks, fewer Zoom calls. Working from cafés that smell like rain.",
    status: "🌿 Growing",
  },
];

const lessons = [
  {
    title: "What quitting taught me",
    body: "Quitting the wrong thing is not weakness. It's the first honest decision you make after a long string of polite ones.",
  },
  {
    title: "Why most business advice feels fake",
    body: "It's optimized for retweets, not for your Tuesday morning. Real advice is boring, contextual, and usually free.",
  },
  {
    title: "Activity vs. progress",
    body: "Being busy is the cheapest costume for being lost. Progress leaves a trail you'd actually want to retrace.",
  },
  {
    title: "The cost of audiences",
    body: "Every audience you build is a slightly smaller room you have to keep performing in. Choose the room carefully.",
  },
  {
    title: "On long-term thinking",
    body: "Plant trees under whose shade you do not expect to sit — but also plant a few you might, just to stay honest.",
  },
  {
    title: "The myth of the morning routine",
    body: "What you do at 9pm shapes your work more than what you do at 6am. Most productivity advice is sleep advice in disguise.",
  },
];

const journey = [
  { year: "2014", title: "Student", body: "Curious, broke, reading everything. Built a blog nobody read." },
  { year: "2017", title: "Freelancer", body: "Said yes to too much. Learned what I actually liked by doing what I didn't." },
  { year: "2019", title: "First business", body: "Built it. Broke it. Closed it. Wrote the lessons down so I'd believe them later." },
  { year: "2021", title: "Digital marketer", body: "Started taking strategy seriously. Stopped chasing tactics. Began to see patterns." },
  { year: "2023", title: "Builder", body: "Less consulting, more shipping. Small products, slow growth, fewer apologies." },
  { year: "2025", title: "Future Farmer", body: "Treating my work like a garden instead of a startup. Patience is the new ambition." },
];

function Home() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteNav />

      <main>
        {/* HERO */}
        <section className="container-prose pt-20 pb-32 md:pt-32 md:pb-40">
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr] md:items-center">
          <div className="animate-rise">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Akram Fortas — Future Farmer
            </p>
            <h1 className="mt-6 font-serif text-5xl leading-[1.02] text-balance text-foreground sm:text-6xl md:text-7xl lg:text-[5.5rem]">
              Not a CEO.<br />
              Not a guru.<br />
              <span className="italic text-[color:var(--color-leaf)]">Just a Future Farmer.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-muted-foreground text-pretty">
              I'm a digital marketer, entrepreneur, and lifelong experimenter trying to
              build meaningful things on the internet — without pretending to have all the answers.
            </p>
            <p className="mt-4 max-w-xl text-sm italic text-muted-foreground">
              Currently planting digital seeds and hoping for actual rain.
            </p>
          </div>

          <div className="flex justify-center md:justify-end">
            <SeedSprout className="w-48 h-64 text-foreground md:w-56 md:h-72" />
          </div>
        </div>
      </section>

      {/* CURRENTLY PLANTING */}
      <SectionHeader
        id="planting"
        eyebrow="Section 02"
        title="Currently planting"
        sub="A handful of seeds, in various states of becoming."
      />
      <section className="container-prose mt-12">
        <ol className="relative border-l hairline pl-6 md:pl-10">
          {planting.map((p, i) => (
            <li key={i} className="mb-10 last:mb-0 group">
              <span className="absolute -left-[5px] mt-2 h-2.5 w-2.5 rounded-full bg-[color:var(--color-leaf)] ring-4 ring-background transition-transform group-hover:scale-125" />
              <div className="flex flex-col gap-1 md:flex-row md:items-baseline md:gap-6">
                <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground md:w-40 md:shrink-0">
                  {p.status}
                </span>
                <div>
                  <h3 className="font-serif text-2xl text-foreground">{p.title}</h3>
                  <p className="mt-1 max-w-2xl text-muted-foreground text-pretty">{p.desc}</p>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* LESSONS */}
      <SectionHeader
        id="lessons"
        eyebrow="Section 03"
        title="Lessons from the field"
        sub="Short notes from the mud. No frameworks. No five-step formulas."
      />
      <section className="container-prose mt-12 grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-3 border hairline">
        {lessons.map((l, i) => (
          <article
            key={i}
            className="bg-background p-8 transition-colors hover:bg-secondary/60"
          >
            <h3 className="font-serif text-2xl text-foreground text-balance">{l.title}</h3>
            <p className="mt-4 text-muted-foreground leading-relaxed text-pretty">{l.body}</p>
          </article>
        ))}
      </section>

      {/* JOURNEY */}
      <SectionHeader
        id="journey"
        eyebrow="Section 04"
        title="The journey, not the highlight reel"
        sub="Evolution over achievements. Each step quieter than the last."
      />
      <section className="container-prose mt-12">
        <div className="space-y-10">
          {journey.map((j) => (
            <div key={j.year} className="grid grid-cols-[5rem_1fr] gap-6 md:grid-cols-[7rem_1fr] md:gap-10">
              <div className="font-mono text-sm text-muted-foreground pt-1">{j.year}</div>
              <div className="border-l hairline pl-6">
                <h3 className="font-serif text-2xl text-foreground">{j.title}</h3>
                <p className="mt-1 max-w-2xl text-muted-foreground text-pretty">{j.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* QUOTE */}
      <section className="container-prose mt-32 mb-20">
        <figure className="mx-auto max-w-3xl text-center">
          <blockquote className="font-serif text-3xl italic leading-snug text-foreground md:text-4xl text-balance">
            &ldquo;Plant trees under whose shade you do not expect to sit.&rdquo;
          </blockquote>
          <figcaption className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            — Greek proverb
          </figcaption>
        </figure>
      </section>
      </main>

      <SiteFooter />
    </div>
  );
}

function SectionHeader({
  id,
  eyebrow,
  title,
  sub,
}: {
  id?: string;
  eyebrow: string;
  title: string;
  sub: string;
}) {
  return (
    <header id={id} className="container-prose mt-24 scroll-mt-24">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">{eyebrow}</p>
      <h2 className="mt-3 font-serif text-4xl text-foreground md:text-5xl text-balance">{title}</h2>
      <p className="mt-3 max-w-2xl text-muted-foreground text-pretty">{sub}</p>
    </header>
  );
}
